import type { ReactNode } from "react";
import { Stack, Text } from "@leabankab/component-library";
import { StepContainer } from "@/components/layout";
import { Heading } from "@/components/ui";

type Props = {
	reason?: string;
	detail?: string;
	flowId?: string;
	action?: ReactNode;
};

const Field = ({ label, value }: { label: string; value: string }) => (
	<Stack gap={0.5}>
		<Text
			render={<span />}
			fontWeight="medium"
			color="text.subtle"
			className="uppercase"
		>
			{label}
		</Text>
		<div className="rounded-xl border border-border-base bg-bg-subtle p-1.5">
			<Text
				textStyle="body-sm"
				className="font-mono break-words whitespace-pre-wrap"
			>
				{value}
			</Text>
		</div>
	</Stack>
);

/**
 * Dev-only error surface. Renders the reason/detail of a terminal or error
 * state that would, in production, redirect the customer off-site. Enabled via
 * SHOW_ERRORS so the redirects can be restored for release.
 */
export const DebugErrorContent = ({
	reason,
	detail,
	flowId,
	action,
}: Props) => (
	<StepContainer>
		<Stack gap={3} align="stretch">
			<Stack gap={1}>
				<Heading level={1} textStyle="heading-lg">
					Debug: flow stopped
				</Heading>
				<Text textStyle="body-sm" color="text.subtle">
					In production this state would redirect off-site. The redirect is
					disabled (SHOW_ERRORS) so the cause is visible.
				</Text>
			</Stack>

			<Field label="Reason" value={reason || "Unknown"} />
			{detail && <Field label="Detail" value={detail} />}
			{flowId && <Field label="Flow ID" value={flowId} />}
			{action}
		</Stack>
	</StepContainer>
);
